import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { ScanLicenseButton } from "./ScanLicenseButton";

/** Formulaire de création de client — réutilisé par le module Clients et par la réservation. */
export default function ClientCreateForm({ defaultStatus = "client", defaultName = "", onCreated, onCancel }) {
  const [form, setForm] = useState({
    name: defaultName || "", email: "", phone: "", notes: "", status: defaultStatus,
    birth_date: "", license_number: "", license_expiry: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post("/vendor/clients", form);
      onCreated(data.client);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="client-create-form">
      <ScanLicenseButton
        onExtract={(d) => setForm((f) => ({
          ...f,
          name: `${d.first_name} ${d.last_name}`.trim() || f.name,
          birth_date: d.birth_date || f.birth_date,
          license_number: d.license_number || f.license_number,
          license_expiry: d.expiry_date || f.license_expiry,
        }))}
      />
      <div>
        <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Type</label>
        <div className="mt-1.5 flex gap-1 p-1 rounded-xl bg-slate-100">
          {[["client", "Client"], ["prospect", "Prospect"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setForm({ ...form, status: k })} className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${form.status === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid={`new-client-status-${k}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nom" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="new-client-name" />
        <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="new-client-email" />
        <Field label="Téléphone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="new-client-phone" />
      </div>
      <div className="rounded-xl bg-slate-50 p-3 space-y-2">
        <div className="text-[10px] uppercase font-bold text-slate-400">Permis de conduire</div>
        <SmallField label="N° de permis" value={form.license_number} onChange={(v) => setForm({ ...form, license_number: v })} testid="new-license-number" />
        <SmallField label="Date de naissance" value={form.birth_date} onChange={(v) => setForm({ ...form, birth_date: v })} placeholder="JJ/MM/AAAA" testid="new-birth-date" />
        <SmallField label="Validité du permis" value={form.license_expiry} onChange={(v) => setForm({ ...form, license_expiry: v })} placeholder="JJ/MM/AAAA" testid="new-license-expiry" />
      </div>
      <div>
        <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Notes</label>
        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-600 focus:border-violet-600 outline-none resize-none" data-testid="new-client-notes" />
      </div>
      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2" data-testid="new-client-error">{error}</div>}
      <div className="flex gap-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="flex-1 h-10 inline-flex items-center justify-center rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50" data-testid="cancel-new-client">
            Annuler
          </button>
        )}
        <button type="submit" disabled={saving} className="flex-1 h-10 inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50" data-testid="submit-new-client">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Créer {form.status === "prospect" ? "le prospect" : "le client"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid, required }) {
  return (
    <div>
      <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-600 focus:border-violet-600 outline-none" data-testid={testid} />
    </div>
  );
}

function SmallField({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-[10px] font-bold text-slate-500">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-0.5 w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-violet-600" data-testid={testid} />
    </div>
  );
}
